import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import TextInput from '../Input/TextInput';
import notification from '../notification';
import { getDemoResetStatus, resetDemoData } from '../../utils/api-helper';
import { notifyDemoResetStarted } from '../../hooks/useDemoResetLock';
import SquareCross from '../../assets/SquareCross.svg';

const ResetDemoModal = ({ isOpen, toggleModal }) => {
  const { userDetails } = useSelector((state) => state?.userDetails);

  const [password, setPassword] = useState('');
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A reset is a long running job, so make sure one is not already running
  // before offering the form
  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        const statusResp = await getDemoResetStatus();
        if (statusResp?.data?.inProgress) {
          notification('warning', 'A demo reset is already in progress');
          toggleModal();
          return;
        }
        setIsCheckingStatus(false);
      } catch (error) {
        notification(
          'error',
          error?.response?.data?.error ||
            'An error occurred, Unable to check the demo reset status. Please try again later.'
        );
        toggleModal();
      }
    })();
  }, [isOpen]);

  const handleClose = () => {
    // Do not let the user close mid request
    if (!isSubmitting) toggleModal();
  };

  const handleResetDemo = async () => {
    if (!password?.trim()) {
      notification('warning', 'Please enter your password');
      return;
    }

    try {
      setIsSubmitting(true);
      // Any resolved response is a success here - axios rejects non 2xx into
      // the catch below, so we don't pin this to a specific status code
      await resetDemoData(password);

      notification(
        'success',
        'Demo reset started. This may take a few minutes, please refresh after a minute.',
        'bottom-center',
        5000
      );
      // Clear before unmounting, a password field vanishing with a value in it
      // is what makes the browser offer to save it
      setPassword('');
      // Only close on a successful response, the blocking overlay takes over
      toggleModal();
      notifyDemoResetStarted();
    } catch (error) {
      notification(
        'error',
        error?.response?.data?.error ||
          'An error occurred, Unable to reset the demo data. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      className={'relative w-full min-w-0 max-w-[540px] px-4 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-md'}
      overlayClassName={'fixed top-0 left-0 right-0 bottom-0 bg-black/80'}
      ariaHideApp={false}
      onRequestClose={handleClose}
      style={{ overlay: { zIndex: 25 } }}
      shouldCloseOnOverlayClick={!isSubmitting}
    >
      {!isSubmitting && (
        <button onClick={handleClose} className='absolute right-6 top-2'>
          <img src={SquareCross} alt='Close' />
        </button>
      )}

      <div className={'bg-white rounded-lg overflow-hidden'}>
        <div className={'flex flex-col p-6'}>
          <span className='text-base font-medium pb-5'>Reset demo data</span>

          {isCheckingStatus ? (
            <span className='text-sm text-neutral-500 py-6 text-center'>
              Checking demo reset status...
            </span>
          ) : (
            <div className='flex flex-col gap-y-4'>
              <p className='text-sm text-neutral-800'>
                This is a demo website. All your data will be reset to the
                initially configured demo data, and every change made since then
                will be reverted. This action cannot be undone.
              </p>
              <p className='text-sm text-error-500'>
                This may take a few minutes to finish. Please refresh the page
                after a minute.
              </p>

              <div className='flex flex-col'>
                <span className='text-neutral-500 text-sm pb-1'>
                  Confirm password for{' '}
                  <span className='text-neutral-800 font-medium break-all'>
                    {userDetails?.user?.email || '-'}
                  </span>
                </span>
                <TextInput
                  name='password'
                  handleChange={(e) => setPassword(e.target.value)}
                  value={password}
                  inputStyle={'bg-neutral-50'}
                  placeholder='Enter your password'
                  type='password'
                  // Not a login field - keep the browser from offering to
                  // autofill or save this one off confirmation password
                  autoComplete='off'
                  disabled={isSubmitting}
                  handleKeyDown={(e) => {
                    if (e.key === 'Enter') handleResetDemo();
                  }}
                />
              </div>

              <div className='flex flex-row justify-end gap-x-3'>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className='text-sm font-medium px-3 py-2 rounded-md border border-neutral-300 text-neutral-800 hover:bg-neutral-100 disabled:opacity-60'
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetDemo}
                  disabled={isSubmitting}
                  className='bg-error-500 text-white text-sm font-medium px-3 py-2 rounded-md disabled:opacity-60'
                >
                  {isSubmitting ? 'Resetting...' : 'Confirm reset'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ResetDemoModal;
